import React from 'react';
import { isEmpty } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router';
import { useNavigate } from 'react-router-dom';
import { useQueryState } from 'react-router-use-location-state';
import { LinearProgress } from '@mui/material';
import { useDeepCompareEffect } from '@fuse/hooks';

import { type MultiButtonOptionType } from '../../buttons/MultiButton';
import { alertSuccessMsg, executeStored } from '../../Utils';
/* eslint-disable-next-line import/no-cycle */
import useLogs, { type LogsType } from '../../hooks/useLogs';
import useModel, { type ModelType, type onBeforeSaveType, type onAfterSaveType } from '../../hooks/useModel';
import useSettings from '../../hooks/useSettings';
import useSidebar from '../../hooks/useSidebar';
import useTabs, { type TabType } from '../../hooks/useTabs';
import PageWrapper from '../../PageWrapper';
import TabsWithMore from '../../TabsWithMore';
import Form from '../../Form';
import UnsavedDialog from '../../dialogs/UnsavedDialog';

import { type FabType } from '../components/PageFab';
import LogsAlert from './components/LogsAlert';

import DetailsPageHeaderWrapper, { type DetailsPageHeaderProps } from './DetailsPageHeader';
import DetailsPageFabs from './DetailsPageFabs';
import DetailsPageRightSidebarWrapper, {
	type DetailsPageRightSidebarProps
} from './components/DetailsPageRightSidebarWrapper';

export type DetailsButtonProps = {
	/** The settings instance */
	settings: any;
	/** The details model */
	model?: any;
	/** If true, the page is editable */
	isEdit: boolean;
	/** The function used to refresh the details */
	updateRefresh: VoidFunction;
	/** The function used to switch between edit mode and normal mode */
	switchEdit: VoidFunction;
	/** The function used to switch between logs mode and normal mode */
	switchShowLogs: VoidFunction;
	/** The function used to toggle the right sidebar */
	toggleRightSidebar: VoidFunction;
	/** The optional additional props */
	additionalProps: any;
};

export interface DetailsPageProps {
	module: string;
	headerOptions?: {
		showAvatar?: DetailsPageHeaderProps['showAvatar'];
		additionalInfo?: DetailsPageHeaderProps['additionalInfo'];
	};
	tabs: TabType<DetailsPageContentProps>[];
	buttons?: {
		itemButtons?: MultiButtonOptionType<DetailsButtonProps>[];
		headerButtons?: MultiButtonOptionType<DetailsButtonProps>[];
		fabs?: FabType<DetailsButtonProps>[];
	};
	events?: {
		onBeforeSave?: onBeforeSaveType;
		onAfterSave?: onAfterSaveType;
	};
	isAdd?: boolean;
	getRightSidebarSections?: (params: DetailsPageRightSidebarProps) => React.ReactNode[];
}

export type DetailsPageContentProps = {
	sysSettings: any;
	model: ModelType['model'];
	isEdit: boolean;
	isAdd: boolean;
	sendChange: ModelType['sendChange'];
	sendFilesChange: ModelType['sendFilesChange'];
	refresh: boolean;
	updateRefresh: VoidFunction;
	logProps: LogsType;
	additionalProps: any;
};

export const DetailsAdditionalPropsContext = React.createContext<any>({});

export default function DetailsPage({
	module,
	headerOptions,
	tabs,
	buttons,
	events,
	isAdd = false,
	getRightSidebarSections
}: DetailsPageProps) {
	const dispatch = useDispatch();
	const { settings, sysSettings, t } = useSettings(module);
	const navigate = useNavigate();
	const location = useLocation();

	const [isLoading, setIsLoading] = React.useState(false);
	const [refresh, setRefresh] = React.useState(true);
	const updateRefresh = () => setRefresh(state => !state);

	//	Gets id, isEdit and baseModel from page
	const { id, tabId: currentTabId } = useParams();
	const [isEdit, setIsEdit] = useQueryState('edit', !!isAdd);

	const uidField = `${settings.modu_short}_uid`;
	const baseModel = { [uidField]: id || uuidv4() };
	const baseModifiedModel = location.state?.model || {};

	//	Creates model props
	const modelProps = useModel({ baseModel, baseModifiedModel, settings, isEdit, isAdd });
	const { model, setModel, save, resetModifiedModel } = modelProps;

	//	Creates additional props
	const [additionalProps, setAdditionalProps] = React.useState<any>({});
	const changeAdditionalProp = (newPropName: string, newProp: any) =>
		setAdditionalProps(prevProps => ({ ...prevProps, [newPropName]: newProp }));

	//	Creates logs props
	const logProps = useLogs({ module, uid: id, updateRefresh });
	const { showLogs, switchShowLogs } = logProps;

	//	Creates page structure
	const rightSidebarSections = getRightSidebarSections?.({ model, isEdit, isAdd, updateRefresh });
	const hasRightSidebar = (rightSidebarSections?.length || 0) > 0;

	const {
		sidebarOpen: rightSidebarOpen,
		setSidebarOpen: setRightSidebarOpen,
		toggleSidebar: toggleRightSidebar
	} = useSidebar({ visible: hasRightSidebar });

	const switchEdit = React.useCallback(() => {
		setIsEdit(state => !state);
		resetModifiedModel();
	}, [resetModifiedModel, setIsEdit]);

	const tabProps = { settings, model, isEdit, updateRefresh };

	const buttonProps: DetailsButtonProps = {
		...tabProps,
		switchEdit,
		switchShowLogs,
		toggleRightSidebar,
		additionalProps
	};

	const moduleToUrl = module?.replace('sys_', '').replaceAll('_', '/');

	const { visibleTabs, currentTab, currentTabFab, changeTab } = useTabs({
		baseUrl: !isAdd ? `/${moduleToUrl}/details/${id}` : `/${moduleToUrl}/add`,
		tabs,
		fabs: buttons?.fabs,
		tabProps,
		fabProps: buttonProps,
		currentTabId
	});

	const handleSubmit = React.useCallback(() => {
		save({
			onBeforeSave: events?.onBeforeSave,
			onAfterSave: events?.onAfterSave,
			onSuccess: params => {
				if (params.isAdd) navigate(`/${moduleToUrl}/details/${params.model[uidField]}`);
				else switchEdit();

				params.dispatch(alertSuccessMsg(params.t, params.isAdd ? 'ALERT_ADD_SUCCESS' : 'ALERT_UPDATE_SUCCESS'));
			}
		});
	}, [events, moduleToUrl, navigate, save, switchEdit, uidField]);

	// 	Gets details model
	useDeepCompareEffect(() => {
		if (!isAdd) {
			setIsLoading(true);
			executeStored({
				action: `st_${settings.modu_title}_get_details`,
				dispatch,
				params: { [uidField]: id },
				successHandler: response => {
					if (!response.data) navigate('/404', { replace: true });
					setModel(response.data);
					setIsLoading(false);
				}
			});
		}
		// Adding navigate to the propsArray triggers an unnecessary reload when the user changes tab
	}, [dispatch, id, isAdd, refresh, setModel, settings.modu_title, uidField]);

	if (!isLoading && !isEmpty(model)) {
		//	Set page title
		const title = !isAdd ? model[`${settings.modu_short}_title`] : `${t('NEW')} ${t(`MODULE_${module}_NAME`)}`;
		if (title) document.title = `${title} | ${import.meta.env.VITE_TITLE}`;

		return (
			<DetailsAdditionalPropsContext.Provider
				value={{ additionalProps, setAdditionalProps, changeAdditionalProp }}
			>
				<PageWrapper
					classes={{ content: 'h-full' }}
					header={
						<DetailsPageHeaderWrapper
							module={module}
							model={model}
							isEdit={isEdit}
							isAdd={isAdd}
							showLogs={showLogs}
							showAvatar={headerOptions?.showAvatar}
							buttons={buttons?.headerButtons || []}
							buttonProps={buttonProps}
							hasRightSidebar={hasRightSidebar}
							rightSidebarOpen={rightSidebarOpen}
							toggleRightSidebar={toggleRightSidebar}
							additionalInfo={headerOptions?.additionalInfo}
						/>
					}
					toolbar={
						visibleTabs?.length > 1 ? (
							<TabsWithMore
								tabs={visibleTabs}
								tabProps={tabProps}
								currentTabId={currentTabId}
								onTabChange={changeTab}
							/>
						) : undefined
					}
					content={
						currentTab && (
							<Form className="h-full w-full" id="detailsPageContent" onSubmit={handleSubmit}>
								<UnsavedDialog hasModelChanged={modelProps.hasModelChanged} />
								<currentTab.content
									sysSettings={sysSettings}
									model={modelProps.model}
									isEdit={isEdit}
									isAdd={isAdd}
									sendChange={modelProps.sendChange}
									sendFilesChange={modelProps.sendFilesChange}
									refresh={refresh}
									updateRefresh={updateRefresh}
									logProps={logProps}
									additionalProps={additionalProps}
								/>
							</Form>
						)
					}
					fab={
						!showLogs && currentTabFab ? (
							<DetailsPageFabs fab={currentTabFab} fabProps={buttonProps} isEdit={isEdit} isAdd={isAdd} />
						) : undefined
					}
					rightSidebarOpen={rightSidebarOpen}
					setRightSidebarOpen={setRightSidebarOpen}
					rightSidebarContent={
						hasRightSidebar ? (
							<DetailsPageRightSidebarWrapper
								rightSidebarSections={rightSidebarSections}
								toggleRightSidebar={toggleRightSidebar}
							/>
						) : undefined
					}
				/>
				<LogsAlert logProps={logProps} />
			</DetailsAdditionalPropsContext.Provider>
		);
	}

	return <LinearProgress />;
}
